import React, { useEffect } from "react";
import {
    Snackbar,
    Slide,
    Paper,
    Typography,
    Chip,
    Box,
    Divider,
    IconButton,
    Stack,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";
import VwHastauyari1DTO from "src/models/HastaUyariModels/VwHastauyari1DTO";
import { translate } from "src/i18n/tHelper";
import { useAppSelector } from "src/store";
import { helper } from "src/utils/helper";
import ParametreGrupEnum from "src/enums/ParametreGrupEnum";
import { useLazyHastaUyari1SorgulaQuery } from "src/services/HastaUyariListesi/HastaUyariListeApi";
import HastaUyariSorguKriterleri from "src/models/HastaUyariModels/HastaUyariSorguKriterleri";
import { Button } from "src/components";
import { useFindAuthByUrl } from "src/utils/authUtils";

const TransitionUp = React.forwardRef(function TransitionUp(
    props: any,
    ref: React.Ref<unknown>
) {
    return (
        <Slide
            {...props}
            ref={ref}
            direction="up"
            timeout={{
                enter: 500,
                exit: 400,
            }}
            easing={{
                enter: "cubic-bezier(0.23, 1, 0.32, 1)",
                exit: "cubic-bezier(0.23, 1, 0.32, 1)",
            }}
        />
    );
});

const translatePath = "pages|hastaUyari";
const FIXED_WIDTH = 400;
const MAX_ACIKLAMA_LENGTH = 120;

interface SingleHastaUyariPopupProps {
    open: boolean;
    onClose: () => void;
    uyari: VwHastauyari1DTO;
    index: number;
    total: number;
};

const SingleHastaUyariPopup = (props: SingleHastaUyariPopupProps): JSX.Element => {
    const { open, onClose, uyari, index, total } = props;
    const parameters = useAppSelector(s => s.parameters)
    const [showFull, setShowFull] = React.useState(false);
    const aciklama = uyari.aciklama || "";
    const isLong = aciklama.length > MAX_ACIKLAMA_LENGTH;
    const shownText = showFull ? aciklama : aciklama.slice(0, MAX_ACIKLAMA_LENGTH);

    return (
        <Snackbar
            open={open}
            onClose={(event, reason) => {
                if (reason === "clickaway" || reason === "escapeKeyDown") return;
                onClose();
            }}
            TransitionComponent={TransitionUp}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            autoHideDuration={null}
            sx={{
                bottom: `32px !important`,
                right: "32px !important",
                zIndex: 1400 + index,
                "& .MuiPaper-root": {
                    animation: open
                        ? "popupOpenAnim 0.5s cubic-bezier(0.23,1,0.32,1) both"
                        : undefined,
                },
                "@keyframes popupOpenAnim": {
                    "0%": { opacity: 0, transform: "translateY(40px) scale(0.97)" },
                    "100%": { opacity: 1, transform: "translateY(0) scale(1)" },
                },
            }}
        >
            <Paper
                elevation={8}
                sx={{
                    width: FIXED_WIDTH,
                    minWidth: FIXED_WIDTH,
                    maxWidth: FIXED_WIDTH,
                    p: 2,
                    borderLeft: uyari.tibbiuyari === "E" ? "6px solid #d32f2f" : "6px solid #ed6c02",
                    position: "relative",
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <WarningAmberIcon color={uyari.tibbiuyari === "E" ? "error" : "warning"} />
                    <Typography variant="h6" sx={{ flex: 1 }}>
                        {translate(translatePath, "Hasta Uyarısı")}
                    </Typography>
                    <Chip
                        label={`${translate(translatePath, "Açık Pencere")}: ${index + 1}/${total}`}
                        color="info"
                        size="small"
                        sx={{ ml: 1 }}
                    />
                    <IconButton
                        size="small"
                        onClick={onClose}
                        sx={{ position: "absolute", top: 4, right: 4 }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Chip
                        label={parameters.data?.filter((f) => f.grupId === ParametreGrupEnum.UyariTipi).find((f) => f.key === uyari.uyaritip)?.value || translate(translatePath, "Bilinmeyen")}
                        color="warning"
                        size="small"
                    />
                    {uyari.fkUyaritip && (
                        <Chip
                            label={parameters.data?.filter((f) => f.grupId === ParametreGrupEnum.UyariGrubu).find((f) => f.key === uyari.fkUyaritip)?.value || "Diğer"}
                            color={uyari.tibbiuyari === "E" ? "error" : "info"}
                            size="small"
                        />
                    )}
                    {uyari.uyaritarih && (
                        <Typography variant="caption" color="text.secondary">
                            {helper.dateTimeValueFormat(uyari.uyaritarih)}
                        </Typography>
                    )}
                    {uyari.tibbiuyari === "E" && (
                        <Chip label={translate(translatePath, "Tıbbi Uyarı")} color="error" size="small" />
                    )}
                </Box>
                <Box>
                    <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{
                            whiteSpace: "pre-line",
                            minHeight: !showFull ? 48 : undefined,
                            maxHeight: !showFull ? 48 : undefined,
                            overflow: !showFull ? "hidden" : undefined,
                            display: "block",
                        }}
                    >
                        <div dangerouslySetInnerHTML={{ __html: shownText }} />
                    </Typography>
                    {isLong && !showFull && (
                        <Button
                            fullWidth={false}
                            variant="text"
                            size="small"
                            onClick={() => setShowFull(true)}
                            sx={{
                                mt: 0.5,
                                fontWeight: 600,
                                minHeight: "unset",
                                p: 0,
                                background: "#fff",
                                position: "relative",
                                zIndex: 1,
                            }}
                        >
                            {`...${translate(translatePath, "devamını oku")}`}
                        </Button>
                    )}
                </Box>
                <Box mt={1}>
                    {uyari.uyaribaslangictarih && (
                        <Typography variant="caption" color="text.secondary">
                            {`${translate(translatePath, "Başlangıç")}: ${helper.dateTimeValueFormat(uyari.uyaribaslangictarih)}`}
                        </Typography>
                    )}
                    {uyari.uyaribitistarih && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                            {`${translate(translatePath, "Bitiş")}: ${helper.dateTimeValueFormat(uyari.uyaribitistarih)}`}
                        </Typography>
                    )}
                </Box>
            </Paper>
        </Snackbar>
    );
};

const HastaUyariPopup = (): JSX.Element => {
    const kimlikId = useAppSelector(s => s.kimlik.activeRow?.id) || 0;
    const tcKNo = useAppSelector(s => s.kimlik.activeRow?.kimlikNo) || "";
    const muracaatId = useAppSelector(s => s.muracaat.activeRow?.id) || 0;
    const authControl = useFindAuthByUrl();

    const [query, queryState] = useLazyHastaUyari1SorgulaQuery();

    useEffect(() => {
        if (kimlikId > 0 || !helper.stringIsNullOrEmpty(tcKNo)) {
            var req: HastaUyariSorguKriterleri = {
                kimlikId: kimlikId,
                tcKimlikNo: tcKNo,
                muracaatId: muracaatId,
                modulId: authControl?.fkGnlModulId
            };
            query(req).unwrap();
        }
    }, [kimlikId, tcKNo]);

    const filterData = (data: VwHastauyari1DTO[]) => {
        if (!Array.isArray(data))
            return [];
        return data;
    };
    const [openList, setOpenList] = React.useState<boolean[]>([]);

    useEffect(() => {
        setOpenList(filterData(queryState.data).map(() => true));
    }, [filterData(queryState.data).length]);

    const handleClose = (idx: number) => {
        setOpenList((prev) => prev.map((v, i) => (i === idx ? false : v)));
    };

    return (
        <>
            {filterData(queryState.data).map(
                (uyari, idx) =>
                    openList[idx] && (
                        <SingleHastaUyariPopup
                            key={uyari.id}
                            open={openList[idx]}
                            onClose={() => handleClose(idx)}
                            uyari={uyari}
                            index={idx}
                            total={filterData(queryState.data).length}
                        />
                    )
            )}
        </>
    );
};

export default HastaUyariPopup;
